import type { AmountDTO } from 'gw-edge-client/dist/types/dto/pc/edge/capabilities/currency/dto';
import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time';
import type {
    AllCoverablesDTO,
    CoverableDTO,
    CoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { MultipleFieldValueInformation } from '../config/flavourConfig';
import type { ConditionExclusionManualTextDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/common';
import { convertLocalDateDTOToDate, convertLocalDateToString } from './dateUtil';
import ConditionsAndExclusionsModal from '../shared_components/ConditionsAndExclusionsModal/ConditionsAndExclusionsModal';
import type {
    AllCoverablesOnsaDTO,
    CoverableOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type { AreaOfDestination_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { AllCoverables, ProductStatus } from '../recoil/types/onsaTypes';
import type {
    AdultAccidentOnsaDTO,
    YoungAccidentOnsaDTO,
    ChildAccidentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/accident';
import type {
    PrivateContentOnsaDTO,
    YouthContentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/content';
import type { CarOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import type { CoverageTextItemRequest } from '../recoil/types/hippoTypes';

const allCoverablesToCoverableDTOArray = (allCoverablesDTO: AllCoverablesDTO): CoverableDTO[] => {
    return Object.entries(allCoverablesDTO).flatMap((x) => x.pop());
};

const allCoverablesOnsaToCoverableDTOArray = (
    allCoverablesOnsaDTO: AllCoverablesOnsaDTO
): CoverableDTO[] => {
    return Object.entries(allCoverablesOnsaDTO).flatMap((x) => x.pop());
};

const isAgricultureCoverable = (coverableDTO: CoverableDTO): boolean => {
    const agricultureLobCodes = ['CTP', 'PRA', 'WOC', 'MAC'];
    return agricultureLobCodes.includes(coverableDTO.lobCode ?? '');
};

const isWocOrCtpCoverable = (coverableDTO: CoverableDTO): boolean => {
    const agricultureLobCodes = ['WOC', 'CTP'];
    return agricultureLobCodes.includes(coverableDTO.lobCode ?? '');
};

const isLocalDateDTO = (
    tableValue: LocalDateDTO | string | AmountDTO | MultipleFieldValueInformation[]
): tableValue is LocalDateDTO => {
    return (tableValue as LocalDateDTO).day !== undefined;
};

const isAmountDTO = (
    tableValue: LocalDateDTO | string | AmountDTO | MultipleFieldValueInformation[]
): tableValue is AmountDTO => {
    return (tableValue as AmountDTO).currency !== undefined;
};

const isMultipleFieldValueInformation = (
    tableValue: LocalDateDTO | string | AmountDTO | MultipleFieldValueInformation[]
): tableValue is MultipleFieldValueInformation[] => {
    return (tableValue as MultipleFieldValueInformation).modalInformation !== undefined;
};

const formatConditionsAndExclusionsToFieldValues = (
    conditionAndExclusionList: ConditionExclusionManualTextDTO[]
): MultipleFieldValueInformation[] => {
    const conditionAndExclusionsFieldValue = conditionAndExclusionList.map((condition) => {
        return {
            modalInformation: {
                modalType: ConditionsAndExclusionsModal,
                modalProps: {
                    title: condition.name ?? '',
                    description: condition.text ?? ''
                }
            },
            title: condition.name ?? ''
        };
    });

    return conditionAndExclusionsFieldValue;
};

const formatStringToMonetaryValueString = (value: string): string => {
    return Number(value).toLocaleString('da-DK') + ' kr.';
};

const formatCoverableFieldValue = (
    fieldValue: LocalDateDTO | AmountDTO | string | MultipleFieldValueInformation[]
): string => {
    if (isLocalDateDTO(fieldValue)) {
        const date = convertLocalDateDTOToDate(fieldValue);
        return convertLocalDateToString(date) ?? '';
    }
    if (isAmountDTO(fieldValue)) {
        const price = fieldValue.amount?.toFixed(2).toString();
        return price?.concat(' kr.').replace('.', ',') ?? '';
    }
    if (isMultipleFieldValueInformation(fieldValue)) {
        return '';
    }

    if (!fieldValue.includes('%') && fieldValue.includes('kr')) {
        const numberString = fieldValue.split(' ')[0];
        const numberStringWithoutDecimals = numberString.split(',')[0];
        const rawNumberString = numberStringWithoutDecimals.replace(/\./g, '');
        if (!isNaN(Number(rawNumberString))) {
            return formatStringToMonetaryValueString(rawNumberString);
        }
    }

    return fieldValue;
};

const getCoverableIndex = (coverables: CoverableOnsaDTO[], fixedId: string): number => {
    return coverables?.findIndex((coverable) => coverable.fixedId === fixedId) ?? -1;
};

const getPropertyToUpdate = (flavourName: string): string => {
    switch (flavourName) {
        case 'Ulykkesforsikring':
            return 'adultAccidents';
        case 'Ulykkesforsikring til unge':
            return 'youngAccidents';
        case 'Børneforsikring':
            return 'childAccidents';
        case 'Katteforsikring':
            return 'cats';
        case 'Hundeforsikring':
            return 'dogs';
        case 'Indboforsikring':
            return 'privateContent';
        case 'Indboforsikring til unge':
            return 'youthContent';
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return 'travels';
        case 'Bilforsikring':
            return 'cars';
        case 'Ejendomsforsikring':
            return 'agricultureProperty';
        case 'Privat løsøre- og ansvarsforsikring':
            return 'agricultureContent';
        case 'Maskinforsikring':
            return 'machinery';
        case 'Arbejdsskadeforsikring':
            return 'workersComp';
        case 'Kennelforsikring':
            return 'kennels';
        case 'Enkeltrejseforsikring':
            return 'singleTripTravel';
        case 'Afbestillingsforsikring til enkeltrejse':
            return 'singleTripCancellation';
        default:
            return '';
    }
};

const getLobToUpdate = (flavourName: string): string => {
    switch (flavourName) {
        case 'Ulykkesforsikring':
            return 'adultAccident';
        case 'Ulykkesforsikring til unge':
            return 'youngAccident';
        case 'Børneforsikring':
            return 'childAccident';
        case 'Katteforsikring':
            return 'cat';
        case 'Hundeforsikring':
            return 'dog';
        case 'Indboforsikring':
            return 'privateContent';
        case 'Indboforsikring til unge':
            return 'youthContent';
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return 'travel';
        case 'Bilforsikring':
            return 'car';
        default:
            return '';
    }
};

const getCoveragePackageFieldsToUpdate = (coverable: CoverableOnsaDTO): object | undefined => {
    switch (coverable.flavourName) {
        case 'Ulykkesforsikring':
            return { insuranceSum: (coverable as AdultAccidentOnsaDTO).insuranceSum };
        case 'Ulykkesforsikring til unge':
            return { insuranceSum: (coverable as YoungAccidentOnsaDTO).insuranceSum };
        case 'Børneforsikring':
            return { insuranceSum: (coverable as ChildAccidentOnsaDTO).insuranceSum };
        case 'Indboforsikring':
            return {
                contentAndBaggageCovDeductible: (coverable as PrivateContentOnsaDTO)
                    .contentAndBaggageCovDeductible
            };
        case 'Indboforsikring til unge':
            return {
                contentAndBaggageCovDeductible: (coverable as YouthContentOnsaDTO)
                    .contentAndBaggageCovDeductible
            };
        case 'Bilforsikring':
            return { deductible: (coverable as CarOnsaDTO).deductible };
        default:
            return undefined;
    }
};

const OnsaflavourOrder: string[] = [
    'privateContent',
    'youthContent',
    'travels',
    'cars',
    'adultAccidents',
    'youngAccidents',
    'childAccidents',
    'dogs',
    'cats'
];

const getCurrentProduct = (coverableList: Record<string, ProductStatus[]>): ProductStatus => {
    const sortedList = Object.fromEntries(
        Object.entries(coverableList).sort(
            ([a], [b]) => OnsaflavourOrder.indexOf(a) - OnsaflavourOrder.indexOf(b)
        )
    );

    const currentProduct = Object.values(sortedList)
        .flatMap((productList) => productList)
        .find((item) => !item.calculated) as ProductStatus;

    return currentProduct;
};

const getOnsaCoverable = (
    coverables: AllCoverablesOnsaDTO,
    flavourName: string,
    fixedId: string
): CoverableOnsaDTO => {
    const propertyToUpdate = getPropertyToUpdate(flavourName);
    const flavour: CoverableOnsaDTO[] =
        coverables[propertyToUpdate as keyof AllCoverablesOnsaDTO] ?? [];
    const coverable = flavour?.find((coverable) => coverable.fixedId === fixedId);
    if (!coverable) return { publicId: '', fixedId: '', lobCode: '', flavourName: '', termIds: [] };
    return coverable;
};

const areaDestination: Record<AreaOfDestination_Top, string> = {
    Worldwide: 'Worldwide',
    OnlyEurope: 'OnlyEurope'
} as const;

const getCoverageFromCoverable = (
    coverages: CoverageDTO[] | undefined,
    coverageType: string
): CoverageDTO | undefined => {
    return coverages?.find((coverage) => coverage.type === coverageType);
};

const getCoverableFromAllOnsaCoverables = (
    coverables: AllCoverablesOnsaDTO,
    fixedId: string,
    lobCode: string
): CoverableDTO | undefined => {
    return (
        allCoverablesOnsaToCoverableDTOArray(coverables).find(
            (cov) => cov.fixedId === fixedId && cov.lobCode === lobCode
        ) ?? {}
    );
};

const shouldImmediatelyWithdrawJobForProduct = (flavourName: string): boolean => {
    switch (flavourName) {
        case 'Indboforsikring':
        case 'Indboforsikring til unge':
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return true;
        default:
            return false;
    }
};

const updateProductStatusList = (
    productStatusList: Record<string, ProductStatus[]>,
    allCoverables: AllCoverables,
    isCalculated = false
): Record<string, ProductStatus[]> => {
    const updatedList: Record<string, ProductStatus[]> = { ...productStatusList };

    for (const property in allCoverables) {
        const coverables = allCoverables[property as keyof AllCoverables];

        if (Array.isArray(coverables) && coverables.length > 0) {
            const newStatuses = [...coverables]
                .sort(
                    (a, b) =>
                        (Object.entries(b).length - Object.entries(a).length ||
                            a.fixedId?.localeCompare(b.fixedId ?? '')) ??
                        0
                )
                .map((coverable) => ({
                    calculated: isCalculated,
                    fixedId: coverable.fixedId ?? '',
                    flavourName: coverable.flavourName ?? '',
                    lobCode: coverable.lobCode ?? ''
                }));
            updatedList[property] = [...(updatedList[property] ?? []), ...newStatuses];
        }
    }
    return updatedList;
};

const formatMonetaryAmountToStringWithoutCurrency = (value: number): string => {
    return value.toLocaleString('da-DK');
};

const isAccCoverableForOtherPerson = (coverable: CoverableDTO): boolean => {
    return (
        coverable.lobCode === 'ACC' &&
        'isForOtherPerson' in coverable &&
        !!coverable.isForOtherPerson
    );
};
const findCarWithMatchingProperty = (
    shoppingBasketCoverables: AllCoverablesDTO,
    onsaCoverables: AllCoverablesOnsaDTO,
    currentCarFixedId: string,
    carProperty: keyof CarDTO & keyof CarOnsaDTO
): CarOnsaDTO | CarDTO | undefined => {
    const carInShoppingBasket = shoppingBasketCoverables.cars?.find(
        (car) => car[carProperty] !== undefined
    );
    const carInOnsa = onsaCoverables.cars?.find(
        (car) => car.fixedId !== currentCarFixedId && car[carProperty] !== undefined
    );

    return carInShoppingBasket || carInOnsa;
};

const getCoverageRequestItems = (
    termIds: string[],
    coverageIds: string[]
): CoverageTextItemRequest[] => {
    return termIds.flatMap((termsId) =>
        coverageIds.map((coverageId) => ({
            termsId,
            coverageId
        }))
    );
};

export {
    allCoverablesToCoverableDTOArray,
    allCoverablesOnsaToCoverableDTOArray,
    isAgricultureCoverable,
    formatCoverableFieldValue,
    formatConditionsAndExclusionsToFieldValues,
    formatStringToMonetaryValueString,
    getCoverableIndex,
    getPropertyToUpdate,
    getOnsaCoverable,
    areaDestination,
    getCoverageFromCoverable,
    isWocOrCtpCoverable,
    getCurrentProduct,
    getCoverableFromAllOnsaCoverables,
    getLobToUpdate,
    getCoveragePackageFieldsToUpdate,
    shouldImmediatelyWithdrawJobForProduct,
    updateProductStatusList,
    formatMonetaryAmountToStringWithoutCurrency,
    isAccCoverableForOtherPerson,
    findCarWithMatchingProperty,
    getCoverageRequestItems
};
